/**
 * This is the fragment shader for the "mask" program.
 * 
 * It determines the color for each pixel, pulling the color from the texture
 * and then adjusting the hue and saturation as needed.
 *
 * We pass in parameters via the "uniforms", including the opacity of the mask,
 * the target hue, and thresholds for hue and saturation). We also pass the
 * texture itself via a uniform.
 */

precision highp float; // Use high precision for floats

varying vec2 vTexCoord; // The texture coordinates, passed in from the vertex shader.
uniform sampler2D uTexture; // The texture we're pulling from (a raster tile image).

uniform float uOpacity; // 0 to 1 (how opaque the mask is)
uniform float uHueTarget; // -1 to disable, 0 to 1 (we adjust hue to be closer to this target)
uniform float uHueThreshold; // 0 to 1 (we fade out colors that have hue below this threshold)
uniform float uSaturationThreshold; // 0 to 1 (we fade out colors that have saturation below this threshold)
uniform float uFadeFactor; // 0 to 1 (how much to fade colors we're excluding)

const float hueAdjustmentFactor = 0.35; // 0 to 1 (how much to adjust hue towards target)

// Function to convert RGB to HSV (so we can adjust hue and saturation)
vec3 rgb2hsv(vec3 c) {
  vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
  vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
  vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));

  float d = q.x - min(q.w, q.y);
  float e = 1.0e-10;
  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}

// Function to convert HSV to RGB (so we can convert back after adjusting hue and saturation)
vec3 hsv2rgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}

void main() {
  // Pull the color from the texture:
  vec4 color = texture2D(uTexture, vTexCoord);

  // Adjust opacity, using the value passed in via `uOpacity`
  color.a *= uOpacity;

  // Convert RGB to HSV
  vec3 hsv = rgb2hsv(color.rgb);

  // Adjust hue and saturation as needed
  if(hsv.s < uSaturationThreshold) {
    color.a *= (1.0 - uFadeFactor); // (fade out colors w/ low saturation)
  } else if(hsv.x < uHueThreshold) {
    color.a *= (1.0 - uFadeFactor); // (fade out colors w/ low hue)
  } else if(uHueTarget != -1.0) {
    hsv.x = hsv.x + (uHueTarget - hsv.x) * hueAdjustmentFactor; // (if uHueTarget is set, adjust hue to be closer to uHueTarget)
  }

  // Convert HSV back to RGB
  vec3 rgb = hsv2rgb(hsv);

  // Apply alpha (we're using `gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)` i.e. premultiplied alpha form)
  gl_FragColor = vec4(rgb.rgb * color.a, color.a);
}
